import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView } from '@/components/base'
import {
  SendBackground,
  SendContent,
  SendCurrencyBottomSheet,
} from '@/features/send-money'
import { useSendLogic } from '@/features/send-money'
import { useSendStore } from '@/store/useSendStore'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'
import { SendWithUserActionButtons } from './components'
import { StickyHeaderNavigation } from '@/components'
import useOffsets from '@/hooks/useOffsets'

interface RecipientData {
  id: string
  name: string
  username?: string
  avatar?: string
  type: 'send' | 'request'
}

export const SendRequestWithUserScreen: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { resetSend } = useSendLogic()
  const offsets = useOffsets()

  // Get recipient data from state
  const recipientData = location.state as RecipientData | null

  const setType = useSendStore((state) => state.setType)
  const type = useSendStore((state) => state.type)
  const setRecipient = useSendStore((state) => state.setRecipient)
  const setSendRequestForm = useSendStore((state) => state.setSendRequestForm)
  const isCurrencyModalOpen = useSendStore((state) => state.isCurrencyModalOpen)
  const setIsCurrencyModalOpen = useSendStore(
    (state) => state.setIsCurrencyModalOpen,
  )

  // Initialize with recipient data
  useEffect(() => {
    if (recipientData) {
      setType(recipientData.type)
      setRecipient({
        id: recipientData.id,
        name: recipientData.name,
      })
      setSendRequestForm({
        recipient: {
          id: recipientData.id,
          name: recipientData.name,
        },
      })
    }
  }, [recipientData, setType, setRecipient, setSendRequestForm])

  // Reset state on unmount
  useEffect(() => {
    return () => {
      resetSend()
    }
  }, [resetSend])

  const handleCloseCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
  }

  // If no recipient data, redirect to regular send-request page
  if (!recipientData) {
    navigate('/send-request')
    return null
  }

  return (
    <BaseView
      className="h-screen flex flex-col overflow-hidden"
      data-component="send-with-user-wrapper"
    >
      <SafeAreaLayout isStretch hasNavBar>
        {/* Gradient background */}
        <SendBackground />

        {/* Header with recipient info */}
        <StickyHeaderNavigation
          title={
            type === 'request'
              ? t('form:actions.request')
              : t('form:actions.send')
          }
          showBackButton={true}
          showBackground={false}
          onBackPress={() => navigate(-1)}
        />

        {/* Main content */}
        <BaseView
          flex={1}
          className="relative z-10 flex flex-col overflow-hidden"
          px={12}
          style={{
            paddingTop: `${70}px`,
            minHeight: 0,
          }}
        >
          {/* Main content - amount input */}
          <SendContent />
        </BaseView>

        {/* Recipient action buttons */}
        <SendWithUserActionButtons recipientData={recipientData} />

        {/* Currency selection modal */}
        <SendCurrencyBottomSheet
          isOpen={isCurrencyModalOpen}
          onClose={handleCloseCurrencyModal}
        />
      </SafeAreaLayout>
    </BaseView>
  )
}
